const CHECKOUT_PREFILL_KEY = "orbiee_checkout_billing_prefill";

export const DEFAULT_CHECKOUT_PHONE = "+1234567890";

/**
 * Parse "801 Market St, San Francisco, CA 94103" into address parts.
 * @param {string} address
 */
export function parseBillingAddress(address) {
  const raw = String(address ?? "").trim();
  if (!raw) {
    return { streetAddress: "", city: "", state: "", zipCode: "" };
  }

  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) {
    return { streetAddress: raw, city: "", state: "", zipCode: "" };
  }

  const streetAddress = parts[0];
  const city = parts.length >= 3 ? parts[1] : "";
  const stateZipPart = parts.length >= 3 ? parts[2] : parts[1];
  const stateZipMatch = stateZipPart.match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);

  return {
    streetAddress,
    city,
    state: stateZipMatch ? stateZipMatch[1].toUpperCase() : stateZipPart,
    zipCode: stateZipMatch ? stateZipMatch[2] : "",
  };
}

/**
 * Parse "MasterCard **** 3971" → card display hints.
 * @param {string} paymentDetail
 */
export function parsePaymentDetail(paymentDetail) {
  const raw = String(paymentDetail ?? "").trim();
  if (!raw) return { cardNumber: "", cardholderName: "" };

  const lastFourMatch = raw.match(/(\d{4})\s*$/);
  const lastFour = lastFourMatch ? lastFourMatch[1] : "";
  const cardNumber = lastFour ? `**** **** **** ${lastFour}` : raw;

  return { cardNumber, cardholderName: "" };
}

/**
 * Map agent billing payload to CheckoutPage field shapes.
 * @param {Record<string, unknown>} billing
 */
export function mapBillingToCheckoutFields(billing) {
  if (!billing || typeof billing !== "object") return null;

  const firstName = String(billing.first_name ?? "").trim();
  const lastName = String(billing.last_name ?? "").trim();
  const email = String(billing.email ?? "").trim();
  const addressTag = String(billing.address_tag ?? "").trim();
  const addressParsed = parseBillingAddress(billing.address);
  const streetWithTag = addressTag
    ? `${addressParsed.streetAddress} (${addressTag})`.trim()
    : addressParsed.streetAddress;

  const payment = parsePaymentDetail(billing.payment_detail);
  const cardholderName = [firstName, lastName].filter(Boolean).join(" ");

  const shipping = {
    firstName,
    lastName,
    email,
    phone: DEFAULT_CHECKOUT_PHONE,
    streetAddress: streetWithTag,
    city: addressParsed.city,
    state: addressParsed.state,
    zipCode: addressParsed.zipCode,
    country: "United States",
  };

  return {
    shipping,
    billing: { ...shipping },
    card: {
      cardNumber: payment.cardNumber,
      cardholderName: cardholderName || payment.cardholderName,
      expirationDate: "",
      cvv: "",
    },
  };
}

export function saveCheckoutPrefill(billing) {
  if (!billing || typeof billing !== "object") return;
  try {
    sessionStorage.setItem(CHECKOUT_PREFILL_KEY, JSON.stringify(billing));
  } catch (err) {
    console.warn("Failed to save checkout prefill:", err);
  }
}

export function loadCheckoutPrefill() {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_PREFILL_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearCheckoutPrefill() {
  try {
    sessionStorage.removeItem(CHECKOUT_PREFILL_KEY);
  } catch (_) {}
}
